import {
  Box,
  Button,
  Card,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Switch,
  Tooltip,
  Typography,
  useTheme,
  Chip,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { H3 } from 'components/Typography';
import { TFunction } from 'i18next';
import { FC, useEffect, useState } from 'react';
import DeleteIcon from 'icons/DeleteIcon';
import { employeePaymentsApi } from 'api';
import { CustomDialog } from 'components/CustomDialog';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import FileUploadInput from 'components/input-fields/FileUploadInput';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import styled from '@emotion/styled';

const PaymentTypeChip = styled(Chip)(({ theme }: { theme: Theme }) => ({
  height: 24,
  borderRadius: 12,
  '&.salary': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    '& .MuiChip-label': {
      fontWeight: 500,
    },
  },
  '&.downpayment': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    '& .MuiChip-label': {
      fontWeight: 500,
    },
  },
}));

interface DataProps {
  paymentsData: Array<{
    date: Date;
    toBePayed: number;
    payed: number;
    remainingCredit: number;
    paymentId: number;
    receiptPath?: string;
    isEncrypted: boolean;
    paymentStaticType: 'DOWNPAYMENT' | 'SALARY';
  }>;
  t: TFunction;
  isUnlimited: boolean;
  fetchData: () => void;
  setOpenPaymentModal: (isOpen: boolean) => void;
  setShowAllPayment: (show: boolean) => void;
}

const PaymentsTable: FC<DataProps> = (props) => {
  const {
    t,
    paymentsData,
    isUnlimited,
    fetchData,
    setOpenPaymentModal,
    setShowAllPayment,
  } = props;
  const theme = useTheme();
  const [showedData, setShowedData] = useState(paymentsData);
  const [deleteId, setDeleteId] = useState(0);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const { encryptionKey } = useEncryption();
  const { setPopoverOpen } = useEncryption();
  const isEncryptionMandatory = localStorage.getItem('isEncryptionMandatory');
  const isDisabled = isEncryptionMandatory === 'true' && encryptionKey === '';

  useEffect(() => {
    console.log(paymentsData);

    // Map the API response to the expected format
    const mappedData = paymentsData.map((payment) => ({
      ...payment,
      paymentStaticType: payment.paymentStaticType || 'SALARY', // Default to SALARY if undefined
    }));
    setShowedData(mappedData);
  }, [paymentsData]);

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
  };

  async function handleDelete(id: number): Promise<void> {
    employeePaymentsApi._delete(id).then(() => {
      fetchData();
    });
  }

  const uplodFile = (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    employeePaymentsApi
      .uploadPayment(id, {
        data: formData,
      })
      .then(fetchData);
  };

  return (
    <Card sx={{ overflow: 'auto' }}>
      <CustomDialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={() => {
          handleDelete(deleteId);
          setDeleteConfirmationOpen(false);
        }}
        title={t('dialog.title', {
          item: t('payment.collectionName'),
        })}
        content={t('dialog.confirmContent.payment')}
        icon={<DeleteForeverIcon />}
        confirmText={t('dialog.confirm')}
        confirmColor='error'
        cancelColor='primary'
      />

      <Grid container textAlign={'center'} minWidth={800}>
        {showedData.length > 0 && (
          <Grid
            container
            item
            xs={12}
            padding={0.5}
            alignItems={'center'}
            bgcolor={theme.palette.action.disabled}
          >
            <Grid item xs={2}>
              <H3>{t('report.paymentsTable.date')}</H3>
            </Grid>
            <Grid item xs={1.5}>
              <H3>{t('report.paymentsTable.type')}</H3>
            </Grid>
            <Grid item xs={1.5}>
              <H3>{t('report.paymentsTable.toBePaid')}</H3>
            </Grid>
            <Grid item xs={1.5}>
              <H3>{t('report.paymentsTable.paid')}</H3>
            </Grid>
            <Grid item xs={1.5}>
              <H3>{t('report.paymentsTable.credit')}</H3>
            </Grid>
            <Grid item xs={2}>
              <H3>{t('report.paymentsTable.paymentSlip')}</H3>
            </Grid>
            <Grid item xs={2}>
              <H3>{t('report.paymentsTable.delete')}</H3>
            </Grid>
          </Grid>
        )}

        {Object.entries(
          showedData.reduce<Record<string, typeof showedData>>((acc, curr) => {
            const date = new Date(curr.date);
            const key = `${date.getFullYear()}-${String(
              date.getMonth() + 1,
            ).padStart(2, '0')}`;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(curr);
            return acc;
          }, {}),
        )
          .sort(([keyA], [keyB]) => keyB.localeCompare(keyA)) // Sort by date descending
          .map(([key, group]) => {
            const totalPayed = group.reduce((sum, item) => sum + item.payed, 0);

            const monthLabel = new Date(key + '-01').toLocaleString('it-IT', {
              month: 'long',
              year: 'numeric',
            });

            return (
              <div key={key} style={{ width: '100%' }}>
                <Grid
                  item
                  xs={12}
                  padding={0.5}
                  paddingLeft={3}
                  bgcolor={theme.palette.action.hover}
                >
                  <Typography
                    variant='subtitle2'
                    fontWeight='bold'
                    textTransform='capitalize'
                  >
                    {monthLabel}
                  </Typography>
                </Grid>
                {group.map((row) => (
                  <Grid
                    item
                    xs={12}
                    container
                    padding={0.5}
                    alignItems={'center'}
                    key={row.paymentId}
                    sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                  >
                    <Grid item xs={2}>
                      <Typography fontWeight={'bold'}>
                        {new Date(row.date).toLocaleDateString('it-IT')}
                      </Typography>
                    </Grid>
                    <Grid item xs={1.5}>
                      <PaymentTypeChip
                        theme={theme}
                        label={
                          row.paymentStaticType === 'SALARY'
                            ? t('payment.salary')
                            : t('payment.downPayment')
                        }
                        className={row.paymentStaticType.toLowerCase()}
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={1.5}>
                      {row.isEncrypted && (
                        <Tooltip title={t('encryption.tooltip')} arrow>
                          <Box
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => {
                              if (encryptionKey === '') {
                                setPopoverOpen(true);
                              }
                            }}
                          >
                            <span style={{ filter: 'blur(2px)', opacity: 1 }}>
                              ・・・ €
                            </span>
                          </Box>
                        </Tooltip>
                      )}
                      {!row.isEncrypted && (
                        <Typography>
                          {row.toBePayed.toLocaleString('de-DE', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                          €
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={1.5}>
                      {row.isEncrypted && (
                        <Tooltip title={t('encryption.tooltip')} arrow>
                          <Box
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => {
                              if (encryptionKey === '') {
                                setPopoverOpen(true);
                              }
                            }}
                          >
                            <span style={{ filter: 'blur(2px)', opacity: 1 }}>
                              ・・・ €
                            </span>
                          </Box>
                        </Tooltip>
                      )}
                      {!row.isEncrypted && (
                        <Typography fontWeight={'bold'} color={'#03BF62'}>
                          {row.payed.toLocaleString('de-DE', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                          €
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={1.5}>
                      {row.isEncrypted && (
                        <Tooltip title={t('encryption.tooltip')} arrow>
                          <Box
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => {
                              if (encryptionKey === '') {
                                setPopoverOpen(true);
                              }
                            }}
                          >
                            <span style={{ filter: 'blur(2px)', opacity: 1 }}>
                              ・・・ €
                            </span>
                          </Box>
                        </Tooltip>
                      )}
                      {!row.isEncrypted && (
                        <Typography color={theme.palette.error.main}>
                          {row.remainingCredit.toLocaleString('de-DE', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                          €
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      {row.receiptPath ? (
                        <Link
                          href={row.receiptPath}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {' '}
                          <FileOpenIcon />{' '}
                        </Link>
                      ) : (
                        <FileUploadInput
                          maxDimension={30}
                          handleOnChange={(e) => {
                            if (e.target.files)
                              uplodFile(row.paymentId, e.target.files[0]);
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton
                        onClick={() => {
                          setDeleteId(row.paymentId);
                          setDeleteConfirmationOpen(true);
                        }}
                      >
                        <DeleteIcon sx={{ color: theme.palette.error.main }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                {/* Summary Row */}
                <Grid
                  item
                  xs={12}
                  container
                  padding={0.5}
                  alignItems={'center'}
                  bgcolor={theme.palette.action.selected}
                  sx={{ borderTop: `2px solid ${theme.palette.divider}` }}
                >
                  <Grid item xs={2}>
                    <Typography fontWeight={'bold'}>
                      {t('report.paymentsTable.total')}
                    </Typography>
                  </Grid>
                  <Grid item xs={1.5}></Grid>
                  <Grid item xs={1.5}></Grid>
                  <Grid item xs={1.5}>
                    <Typography fontWeight={'bold'} color={'#03BF62'}>
                      {totalPayed.toLocaleString('de-DE', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                      €
                    </Typography>
                  </Grid>
                  <Grid item xs={1.5}></Grid>
                  <Grid item xs={4}></Grid>
                </Grid>
              </div>
            );
          })}
        <Grid
          xs={12}
          item
          container
          paddingBottom={2}
          paddingTop={showedData.length > 0 ? 0 : 2}
          alignItems={'center'}
        >
          <Grid item xs={2} justifyItems={'center'} paddingLeft={3}>
            {isUnlimited && (
              <FormControlLabel
                label={t('report.paymentsTable.showAllPayments')}
                labelPlacement='start'
                control={
                  <Switch
                    size='small'
                    onChange={(e) => {
                      setShowAllPayment(e.target.checked);
                    }}
                  />
                }
              />
            )}
          </Grid>

          <Grid item xs={8} paddingRight={3}>
            <Tooltip
              title={isDisabled ? t('encryption.buttonDisabled') : ''}
              disableHoverListener={!isDisabled}
            >
              <div>
                <Button
                  size='large'
                  color='primary'
                  variant='contained'
                  onClick={() => {
                    setOpenPaymentModal(true);
                  }}
                  disabled={isDisabled}
                >
                  {t('payment.addPayment')}
                </Button>
              </div>
            </Tooltip>
          </Grid>

          <Grid item xs={2}></Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export default PaymentsTable;
